import { useEffect, useRef, useMemo } from 'react';
import { useEmployeeStore } from '../store/employeeStore';
import { useAccountBookStore } from '../store/accountBookStore';
import { onEmployeesSnapshot } from '../services/firestoreService';
import type { Unsubscribe } from 'firebase/firestore';

// 전역 플래그로 중복 fetch 방지 (가계부별로 관리)
let globalFetchInProgress = false;
const globalFetchedKeys = new Set<string>();

// 전역 캐시 초기화 함수 (외부에서 호출 가능)
export const clearEmployeeDataCache = () => {
  globalFetchedKeys.clear();
  globalFetchInProgress = false;
};

export const useEmployeeData = () => {
  // 필요한 값만 구독
  const employees = useEmployeeStore((state) => state.employees);
  const employeesLoading = useEmployeeStore((state) => state.employeesLoading);
  const error = useEmployeeStore((state) => state.error);
  const fetchEmployees = useEmployeeStore((state) => state.fetchEmployees);
  const setEmployees = useEmployeeStore((state) => state.setEmployees);
  
  const userId = useAccountBookStore((state) => state.userId);
  const currentAccountNumber = useAccountBookStore((state) => state.currentAccountNumber);
  const currentAccountBook = useAccountBookStore((state) => state.currentAccountBook);
  const isInitialized = useAccountBookStore((state) => state.isInitialized);
  const storeLoading = useAccountBookStore((state) => state.isLoading);
  
  // accountNumber를 useMemo로 메모이제이션 (currentAccountNumber 우선, 없으면 currentAccountBook 사용)
  const accountNumber = useMemo(() => 
    currentAccountNumber || currentAccountBook?.accountNumber, 
    [currentAccountNumber, currentAccountBook?.accountNumber]
  );
  
  const isMountedRef = useRef(true);
  const prevAccountNumberRef = useRef<number | undefined>(undefined);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  // 초기 데이터 로드 (일회성)
  useEffect(() => {
    if (!isInitialized || storeLoading || !userId || !accountNumber) {
      return;
    }

    const fetchKey = `${userId}-${accountNumber}`;
    
    // 가계부 번호가 바뀌면 완전히 초기화 (새 데이터 로드를 위해)
    const accountNumberChanged = prevAccountNumberRef.current !== undefined && 
                                  prevAccountNumberRef.current !== accountNumber;
    
    if (accountNumberChanged) {
      // 모든 캐시 키 초기화
      globalFetchedKeys.clear();
      globalFetchInProgress = false;
    }
    
    prevAccountNumberRef.current = accountNumber;
    
    // 전역적으로 이미 fetch했거나 진행 중이면 스킵 (단, 가계부 전환 직후는 제외)
    if (!accountNumberChanged && (globalFetchedKeys.has(fetchKey) || globalFetchInProgress)) {
      return;
    }

    globalFetchInProgress = true;
    globalFetchedKeys.add(fetchKey);

    // 초기 데이터 로드
    fetchEmployees(userId!, accountNumber!).finally(() => {
      if (isMountedRef.current) {
        globalFetchInProgress = false;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, storeLoading, userId, accountNumber]);

  // 실시간 리스너 설정 (항상 활성화)
  useEffect(() => {
    if (!isInitialized || !userId || !accountNumber) {
      return;
    }

    // 기존 리스너가 있으면 먼저 해제
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // 실시간 리스너 설정
    unsubscribeRef.current = onEmployeesSnapshot(
      userId!,
      (updatedEmployees) => {
        // accountNumber로 필터링
        const filteredEmployees = updatedEmployees.filter(
          (emp: any) => emp.account_number === accountNumber || emp.accountNumber === accountNumber
        );
        setEmployees(filteredEmployees);
      },
      {
        whereConditions: [
          { field: 'account_number', operator: '==', value: accountNumber }
        ],
        orderByField: 'createdAt',
        orderDirection: 'desc'
      }
    );

    // 정리 함수
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, accountNumber, isInitialized]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { employees, loading: employeesLoading, error };
};
